<script setup>
import { ref, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import pipelineAutomationsAPI from 'dashboard/api/pipelineAutomations';
import Button from 'dashboard/components-next/button/Button.vue';
import { default as FluentIcon } from 'shared/components/FluentIcon/Icon.vue';
import dashboardIcons from 'shared/components/FluentIcon/dashboard-icons.json';

const emit = defineEmits(['create', 'edit', 'templates', 'history']);

const store = useStore();
const { t } = useI18n();
const { showAlert } = useAlert();
const automations = ref([]);
const loading = ref(true);
const togglingId = ref(null);
const exportingId = ref(null);
const duplicatingId = ref(null);
const isImporting = ref(false);
const importFileInput = ref(null);

const userIsAdmin = computed(
  () => store.getters.getCurrentUser?.role === 'administrator'
);

const fetchAutomations = async () => {
  loading.value = true;
  try {
    const { data } = await pipelineAutomationsAPI.get();
    automations.value = Array.isArray(data) ? data : [];
  } catch {
    automations.value = [];
  } finally {
    loading.value = false;
  }
};

onMounted(fetchAutomations);

const handleToggle = async automation => {
  if (togglingId.value === automation.id) return;
  togglingId.value = automation.id;
  const originalActive = automation.active;
  try {
    await pipelineAutomationsAPI.update(automation.id, {
      active: !originalActive,
    });
    automation.active = !originalActive;
  } catch {
    // revert is automatic since we didn't flip before success
  } finally {
    togglingId.value = null;
  }
};

const triggerIconMap = {
  event: 'i-lucide-zap',
  scheduled: 'i-lucide-clock',
  webhook: 'i-lucide-webhook',
  manual: 'i-lucide-mouse-pointer-click',
};

const accentColor = automation => {
  if (!automation.active) return 'bg-n-slate-6';
  const status = automation.stats?.last_execution_status;
  if (!automation.stats?.total_executions) return 'bg-n-amber-8';
  if (status === 'completed') return 'bg-n-teal-9';
  if (status === 'failed') return 'bg-n-ruby-9';
  return 'bg-n-slate-6';
};

const relativeTime = dateStr => {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)
    return t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.TIME_JUST_NOW');
  if (mins < 60)
    return t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.TIME_MINUTES_AGO', {
      count: mins,
    });
  const hours = Math.floor(mins / 60);
  if (hours < 24)
    return t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.TIME_HOURS_AGO', {
      count: hours,
    });
  return t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.TIME_DAYS_AGO', {
    count: Math.floor(hours / 24),
  });
};

const formatAvgTime = ms => {
  if (!ms) return '';
  const secs = (ms / 1000).toFixed(1);
  return `~${secs}s`;
};

// Delete confirmation via inline modal (substitui window.confirm — quebrava
// i18n e estética). State é mantido local; abre o modal em handleDelete e
// confirma via performDelete.
const automationToDelete = ref(null);

const handleDelete = automation => {
  automationToDelete.value = automation;
};

const dismissDelete = () => {
  automationToDelete.value = null;
};

const performDelete = async () => {
  const automation = automationToDelete.value;
  if (!automation) return;
  automationToDelete.value = null;
  try {
    await pipelineAutomationsAPI.destroy(automation.id);
    automations.value = automations.value.filter(a => a.id !== automation.id);
    showAlert(t('PIPELINE.FLOW_BUILDER.DELETE_SUCCESS'));
  } catch {
    showAlert(t('PIPELINE.FLOW_BUILDER.DELETE_ERROR'));
  }
};

const handleExport = async automation => {
  if (exportingId.value === automation.id) return;
  exportingId.value = automation.id;
  try {
    const { data } = await pipelineAutomationsAPI.exportAutomation(
      automation.id
    );
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeName = (automation.name || 'automation')
      .replace(/[^a-z0-9_-]/gi, '_')
      .toLowerCase();
    link.href = url;
    link.download = `${safeName}_automation.json`;
    link.click();
    URL.revokeObjectURL(url);
    showAlert(
      t('PIPELINE.FLOW_BUILDER.EXPORT_SUCCESS') ||
        'Automação exportada com sucesso!'
    );
  } catch {
    showAlert(
      t('PIPELINE.FLOW_BUILDER.EXPORT_ERROR') || 'Erro ao exportar automação'
    );
  } finally {
    exportingId.value = null;
  }
};

const handleDuplicate = async automation => {
  if (duplicatingId.value === automation.id) return;
  duplicatingId.value = automation.id;
  try {
    const { data } = await pipelineAutomationsAPI.duplicate(automation.id);
    // Insert the clone right after the original so the user sees it without
    // scrolling/refetching. The backend returns the full automation payload
    // (including stats: zeroed for a fresh clone).
    const idx = automations.value.findIndex(a => a.id === automation.id);
    if (idx === -1) {
      automations.value.unshift(data);
    } else {
      automations.value.splice(idx + 1, 0, data);
    }
    showAlert(t('PIPELINE.FLOW_BUILDER.DUPLICATE_SUCCESS'));
  } catch {
    showAlert(t('PIPELINE.FLOW_BUILDER.DUPLICATE_ERROR'));
  } finally {
    duplicatingId.value = null;
  }
};

const openImportDialog = () => {
  importFileInput.value?.click();
};

const handleImportFile = async event => {
  const file = event.target.files?.[0];
  if (!file) return;
  // Reset input so same file can be re-selected
  event.target.value = '';

  isImporting.value = true;
  try {
    const text = await file.text();
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      showAlert(
        t('PIPELINE.FLOW_BUILDER.IMPORT_INVALID_JSON') ||
          'Arquivo JSON inválido'
      );
      return;
    }

    // Accept both wrapped { automation: {...} } and bare export format
    const automationData = parsed.automation ?? parsed;

    const { data } =
      await pipelineAutomationsAPI.importAutomation(automationData);
    showAlert(
      t('PIPELINE.FLOW_BUILDER.IMPORT_SUCCESS') ||
        'Automação importada! Revise e ative quando estiver pronto.'
    );
    emit('edit', data.id);
  } catch (error) {
    const msg =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      t('PIPELINE.FLOW_BUILDER.IMPORT_ERROR') ||
      'Erro ao importar automação';
    showAlert(msg);
  } finally {
    isImporting.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-background">
    <!-- Header -->
    <header
      class="flex items-center justify-between p-6 border-b border-n-weak"
    >
      <div>
        <h1 class="text-heading-1 text-n-slate-12">
          {{ t('PIPELINE.FLOW_BUILDER.TITLE') }}
        </h1>
        <p class="text-label-small text-n-slate-11 mt-1">
          {{ t('PIPELINE.FLOW_BUILDER.SUBTITLE') }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <!-- Hidden file input for import -->
        <input
          ref="importFileInput"
          type="file"
          accept=".json"
          class="hidden"
          @change="handleImportFile"
        />

        <Button variant="ghost" color="slate" @click="emit('templates')">
          <template #icon>
            <FluentIcon icon="apps" size="18" :icons="dashboardIcons" />
          </template>
          {{ t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.TEMPLATES_BUTTON') }}
        </Button>
        <Button
          v-if="userIsAdmin"
          variant="ghost"
          color="slate"
          :disabled="isImporting"
          @click="openImportDialog"
        >
          <template #icon>
            <span class="i-lucide-upload size-4" />
          </template>
          {{
            isImporting
              ? '...'
              : t('PIPELINE.FLOW_BUILDER.IMPORT') || 'Importar'
          }}
        </Button>
        <Button
          v-if="userIsAdmin"
          variant="solid"
          color="blue"
          @click="emit('create')"
        >
          <template #icon>
            <FluentIcon icon="add" size="20" :icons="dashboardIcons" />
          </template>
          {{ t('PIPELINE.AUTOMATIONS.ADD') }}
        </Button>
      </div>
    </header>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto p-6">
      <!-- Section heading for screen readers — bridges the h1 -> card h3 gap -->
      <h2 class="sr-only">{{ t('PIPELINE.FLOW_BUILDER.LIST_HEADING') }}</h2>
      <!-- Loading skeleton -->
      <div v-if="loading" class="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div
          v-for="n in 4"
          :key="n"
          class="rounded-xl outline outline-1 outline-n-weak bg-n-solid-2 p-5 animate-pulse flex gap-2"
        >
          <div class="w-1 bg-n-alpha-2 rounded self-stretch flex-shrink-0" />
          <div class="flex-1 space-y-3">
            <div class="h-4 bg-n-alpha-2 rounded w-2/3" />
            <div class="h-3 bg-n-alpha-2 rounded w-1/2" />
            <div class="flex gap-3">
              <div class="h-3 bg-n-alpha-2 rounded w-20" />
              <div class="h-3 bg-n-alpha-2 rounded w-16" />
              <div class="h-3 bg-n-alpha-2 rounded w-12" />
            </div>
            <div class="h-8 bg-n-alpha-2 rounded w-1/3" />
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!automations.length"
        class="flex flex-col items-center justify-center h-96 text-center"
      >
        <div
          class="flex items-center justify-center w-20 h-20 mb-6 rounded-2xl bg-n-alpha-1"
        >
          <span class="i-lucide-workflow size-10 text-n-slate-8" />
        </div>
        <h3 class="text-heading-3 text-n-slate-12 mb-2">
          {{ t('PIPELINE.AUTOMATIONS.EMPTY_TITLE') }}
        </h3>
        <p class="text-n-slate-11 max-w-md mb-6">
          {{ t('PIPELINE.AUTOMATIONS.EMPTY_DESCRIPTION') }}
        </p>
        <div class="flex items-center gap-3">
          <Button
            variant="ghost"
            color="slate"
            size="large"
            @click="emit('templates')"
          >
            <template #icon>
              <FluentIcon icon="apps" size="20" :icons="dashboardIcons" />
            </template>
            {{ t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.TEMPLATES_BUTTON') }}
          </Button>
          <Button
            v-if="userIsAdmin"
            variant="solid"
            color="blue"
            size="large"
            @click="emit('create')"
          >
            <template #icon>
              <FluentIcon icon="add" size="20" :icons="dashboardIcons" />
            </template>
            {{ t('PIPELINE.AUTOMATIONS.ADD') }}
          </Button>
        </div>
      </div>

      <!-- Automations Grid -->
      <div v-else class="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div
          v-for="automation in automations"
          :key="automation.id"
          class="relative flex rounded-xl outline outline-1 overflow-hidden transition-all duration-200"
          :class="
            automation.active
              ? 'outline-n-weak bg-n-solid-2 hover:shadow-md'
              : 'outline-n-weak bg-n-solid-2 opacity-70'
          "
        >
          <!-- Left accent bar -->
          <div
            class="w-1.5 flex-shrink-0 self-stretch rounded-r"
            :class="accentColor(automation)"
          />

          <!-- Card body -->
          <div class="flex-1 p-5 flex flex-col gap-3">
            <!-- Header row: icon + name + badge + toggle -->
            <div class="flex items-start justify-between gap-3">
              <div class="flex items-center gap-2 flex-1 min-w-0">
                <span
                  class="size-4 text-n-slate-9 flex-shrink-0"
                  :class="[
                    triggerIconMap[automation.trigger_type] || 'i-lucide-zap',
                  ]"
                />
                <h3
                  class="text-sm font-semibold text-n-slate-12 truncate"
                  :title="automation.name"
                >
                  {{ automation.name }}
                </h3>
              </div>
              <div class="flex items-center gap-2 flex-shrink-0">
                <span
                  class="px-2 py-0.5 text-xs font-medium rounded-full"
                  :class="
                    automation.active
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-alpha-2 text-n-slate-11'
                  "
                >
                  {{
                    automation.active
                      ? t('PIPELINE.AUTOMATIONS.STATUS.ACTIVE')
                      : t('PIPELINE.AUTOMATIONS.STATUS.INACTIVE')
                  }}
                </span>
                <!-- Toggle -->
                <button
                  role="switch"
                  :aria-checked="automation.active ? 'true' : 'false'"
                  :aria-label="
                    t(
                      automation.active
                        ? 'PIPELINE.FLOW_BUILDER.TOGGLE_OFF_ARIA'
                        : 'PIPELINE.FLOW_BUILDER.TOGGLE_ON_ARIA',
                      { name: automation.name }
                    )
                  "
                  class="flex-shrink-0 relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-1 before:absolute before:-inset-1.5 before:content-['']"
                  :class="[
                    automation.active ? 'bg-n-brand' : 'bg-n-alpha-2',
                    togglingId === automation.id
                      ? 'opacity-50 cursor-not-allowed'
                      : 'cursor-pointer',
                  ]"
                  :disabled="togglingId === automation.id"
                  @click="handleToggle(automation)"
                >
                  <span
                    class="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform"
                    :class="
                      automation.active
                        ? 'translate-x-[18px]'
                        : 'translate-x-0.5'
                    "
                  />
                </button>
              </div>
            </div>

            <!-- Description -->
            <p
              v-if="automation.description"
              class="text-xs text-n-slate-11 line-clamp-2"
            >
              {{ automation.description }}
            </p>

            <!-- Stats zone -->
            <div v-if="automation.stats" class="border-t border-n-weak pt-3">
              <!-- Never executed -->
              <p
                v-if="!automation.stats.total_executions"
                class="text-xs text-n-slate-9 flex items-center gap-1.5"
              >
                <span class="i-lucide-calendar-x size-3.5" />
                {{
                  t('PIPELINE.FLOW_BUILDER.NEVER_EXECUTED') || 'Nunca executada'
                }}
              </p>

              <!-- Has executions -->
              <div v-else class="space-y-2">
                <!-- Stats row 1 -->
                <div class="flex items-center gap-4 text-xs text-n-slate-11">
                  <span class="flex items-center gap-1">
                    <span class="i-lucide-play-circle size-3.5" />
                    {{ automation.stats.total_executions }}
                    {{
                      t('PIPELINE.FLOW_BUILDER.EXECUTIONS_LABEL') || 'execuções'
                    }}
                  </span>
                  <span
                    v-if="automation.stats.success_rate !== null"
                    class="flex items-center gap-1 font-medium"
                    :class="
                      automation.stats.success_rate >= 80
                        ? 'text-n-teal-11'
                        : automation.stats.success_rate >= 50
                          ? 'text-n-amber-11'
                          : 'text-n-ruby-11'
                    "
                  >
                    <span class="i-lucide-check-circle size-3.5" />
                    {{ automation.stats.success_rate }}%
                    {{ t('PIPELINE.FLOW_BUILDER.SUCCESS_LABEL') || 'sucesso' }}
                  </span>
                  <span
                    v-if="automation.stats.last_executed_at"
                    class="flex items-center gap-1 ml-auto"
                  >
                    <span class="i-lucide-clock size-3.5" />
                    {{ relativeTime(automation.stats.last_executed_at) }}
                  </span>
                </div>

                <!-- Stats row 2: avg time + sparkline -->
                <div class="flex items-center justify-between">
                  <span
                    v-if="automation.stats.avg_execution_time_ms"
                    class="text-xs text-n-slate-9"
                  >
                    {{ formatAvgTime(automation.stats.avg_execution_time_ms) }}
                  </span>
                  <!-- Last 7 runs sparkline -->
                  <div
                    v-if="automation.stats.last_7_runs?.length"
                    class="flex items-center gap-0.5 ml-auto"
                    :title="
                      t('PIPELINE.FLOW_BUILDER.LAST_RUNS_TITLE') ||
                      'Últimas execuções'
                    "
                  >
                    <div
                      v-for="(run, i) in automation.stats.last_7_runs"
                      :key="i"
                      class="w-2 h-2 rounded-full"
                      :class="{
                        'bg-n-teal-9': run === 'completed',
                        'bg-n-ruby-9': run === 'failed',
                        'bg-n-slate-5': run === 'running' || run === 'pending',
                      }"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex items-center gap-2 pt-1">
              <Button
                variant="ghost"
                color="slate"
                size="sm"
                class="flex-1"
                @click="emit('edit', automation.id)"
              >
                <template #icon>
                  <span class="i-lucide-pencil size-3.5" />
                </template>
                {{ t('PIPELINE.FLOW_BUILDER.EDIT_FLOW') }}
              </Button>
              <Button
                variant="ghost"
                color="slate"
                size="sm"
                :title="t('PIPELINE.FLOW_BUILDER.VIEW_HISTORY')"
                :aria-label="t('PIPELINE.FLOW_BUILDER.VIEW_HISTORY')"
                @click="emit('history', automation.id)"
              >
                <template #icon>
                  <span class="i-lucide-history size-4" />
                </template>
              </Button>
              <Button
                variant="ghost"
                color="slate"
                size="sm"
                :disabled="duplicatingId === automation.id"
                :title="t('PIPELINE.FLOW_BUILDER.DUPLICATE')"
                :aria-label="t('PIPELINE.FLOW_BUILDER.DUPLICATE')"
                @click="handleDuplicate(automation)"
              >
                <template #icon>
                  <span
                    :class="
                      duplicatingId === automation.id
                        ? 'i-lucide-loader-2 animate-spin'
                        : 'i-lucide-copy'
                    "
                    class="size-4"
                  />
                </template>
              </Button>
              <Button
                variant="ghost"
                color="slate"
                size="sm"
                :disabled="exportingId === automation.id"
                :title="t('PIPELINE.FLOW_BUILDER.EXPORT') || 'Exportar'"
                :aria-label="t('PIPELINE.FLOW_BUILDER.EXPORT') || 'Exportar'"
                @click="handleExport(automation)"
              >
                <template #icon>
                  <span class="i-lucide-download size-4" />
                </template>
              </Button>
              <Button
                variant="ghost"
                color="ruby"
                size="sm"
                :title="t('PIPELINE.FLOW_BUILDER.DELETE_ARIA')"
                :aria-label="t('PIPELINE.FLOW_BUILDER.DELETE_ARIA')"
                @click="handleDelete(automation)"
              >
                <template #icon>
                  <FluentIcon icon="delete" size="16" :icons="dashboardIcons" />
                </template>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete confirmation modal (substitui window.confirm) -->
    <div
      v-if="automationToDelete"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      @click.self="dismissDelete"
    >
      <div
        class="bg-n-solid-2 rounded-xl shadow-xl p-6 min-w-[400px] max-w-[90vw] outline outline-1 outline-n-weak"
      >
        <h3 class="text-heading-3 text-n-slate-12 mb-2">
          {{ t('PIPELINE.AUTOMATIONS.DELETE.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 mb-4">
          {{
            t('PIPELINE.AUTOMATIONS.DELETE.DESCRIPTION', {
              name: automationToDelete.name,
            })
          }}
        </p>
        <div class="flex justify-end gap-3">
          <button
            class="px-4 py-2 rounded-md bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 transition-colors text-sm font-medium focus:outline-none focus:ring-2 focus:ring-n-slate-6"
            @click="dismissDelete"
          >
            {{ t('PIPELINE.FLOW_BUILDER.CANCEL_KEEP_EDITING') }}
          </button>
          <button
            class="px-4 py-2 rounded-md bg-n-ruby-9 text-white hover:bg-n-ruby-10 transition-colors text-sm font-medium focus:outline-none focus:ring-2 focus:ring-n-ruby-6"
            @click="performDelete"
          >
            {{ t('PIPELINE.AUTOMATIONS.DELETE.CONFIRM') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
